import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import IntroSequence from './IntroSequence';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GENRE_ICONS = [
  { icon: '🏰', x: '8%',  y: '12%', size: 36, delay: 0    },
  { icon: '🃏', x: '88%', y: '8%',  size: 30, delay: 0.4  },
  { icon: '🎯', x: '6%',  y: '75%', size: 32, delay: 0.8  },
  { icon: '🎲', x: '91%', y: '70%', size: 28, delay: 0.2  },
  { icon: '🏆', x: '15%', y: '45%', size: 24, delay: 1.1  },
  { icon: '🔮', x: '82%', y: '42%', size: 28, delay: 0.6  },
  { icon: '🌟', x: '50%', y: '5%',  size: 22, delay: 1.4  },
  { icon: '🎮', x: '75%', y: '88%', size: 20, delay: 0.9  },
  { icon: '👑', x: '22%', y: '90%', size: 22, delay: 1.6  },
  { icon: '🛡', x: '55%', y: '92%', size: 24, delay: 0.3  },
];

export default function LoginScreen({ onLogin }) {
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(false);
  const [showIntro, setShowIntro] = useState(() => sessionStorage.getItem('sg_intro') !== 'done');
  const navigate = useNavigate();

  const closeIntro = () => { sessionStorage.setItem('sg_intro', 'done'); setShowIntro(false); };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!username.trim()) { toast.error('Inserisci una username'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { username: username.trim() });
      onLogin(res.data);
      toast.success(`Benvenuto, ${res.data.username}!`);
      navigate('/games');
    } catch {
      toast.error('Accesso fallito. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100dvh',
      background: 'linear-gradient(135deg, #0f0c29 0%, #302b63 45%, #24243e 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '20px 16px', position: 'relative', overflow: 'hidden',
    }}>
      {/* Background blobs */}
      <div style={{ position:'absolute', inset:0, pointerEvents:'none' }}>
        <motion.div animate={{ scale:[1,1.15,1], opacity:[0.5,0.8,0.5] }} transition={{ duration:6, repeat:Infinity }}
          style={{ position:'absolute', top:'10%', left:'20%', width:400, height:400, borderRadius:'50%', background:'radial-gradient(circle,rgba(139,92,246,0.35),transparent 70%)', filter:'blur(40px)' }} />
        <motion.div animate={{ scale:[1.1,1,1.1], opacity:[0.4,0.7,0.4] }} transition={{ duration:8, repeat:Infinity, delay:2 }}
          style={{ position:'absolute', bottom:'10%', right:'15%', width:350, height:350, borderRadius:'50%', background:'radial-gradient(circle,rgba(236,72,153,0.3),transparent 70%)', filter:'blur(40px)' }} />
        <motion.div animate={{ scale:[1,1.2,1], opacity:[0.3,0.6,0.3] }} transition={{ duration:7, repeat:Infinity, delay:1 }}
          style={{ position:'absolute', top:'40%', right:'30%', width:280, height:280, borderRadius:'50%', background:'radial-gradient(circle,rgba(59,130,246,0.3),transparent 70%)', filter:'blur(35px)' }} />
        <div style={{ position:'absolute', inset:0, backgroundImage:'linear-gradient(rgba(255,255,255,0.03) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.03) 1px,transparent 1px)', backgroundSize:'60px 60px' }} />
      </div>

      {/* Floating icons */}
      {GENRE_ICONS.map((g, i) => (
        <motion.div key={i}
          style={{ position:'absolute', left:g.x, top:g.y, fontSize:g.size, pointerEvents:'none', userSelect:'none', zIndex:0 }}
          animate={{ y:[0,-12,0], opacity:[0.2,0.5,0.2], rotate:[0,5,-5,0] }}
          transition={{ duration:4+i*0.4, delay:g.delay, repeat:Infinity, ease:'easeInOut' }}
        >
          {g.icon}
        </motion.div>
      ))}

      <motion.div
        initial={{ opacity:0, y:30, scale:0.95 }}
        animate={{ opacity:1, y:0, scale:1 }}
        transition={{ duration:0.6, ease:'easeOut' }}
        style={{ position:'relative', zIndex:1, width:'100%', maxWidth:420 }}
      >
        <div style={{
          background:'rgba(255,255,255,0.07)',
          backdropFilter:'blur(24px)',
          WebkitBackdropFilter:'blur(24px)',
          borderRadius:24,
          border:'1px solid rgba(255,255,255,0.15)',
          boxShadow:'0 32px 64px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.1)',
          padding:'40px 36px 36px',
          position:'relative', overflow:'hidden',
        }}>
          <div style={{ position:'absolute', top:0, left:0, right:0, height:'35%', background:'linear-gradient(to bottom,rgba(255,255,255,0.06),transparent)', borderRadius:'24px 24px 0 0', pointerEvents:'none' }} />

          <div style={{ textAlign:'center', marginBottom:32 }}>
            <motion.div
              animate={{ rotate:[0,5,-5,0] }}
              transition={{ duration:4, repeat:Infinity, ease:'easeInOut' }}
              style={{ display:'inline-flex', alignItems:'center', justifyContent:'center', width:60, height:60, borderRadius:16, background:'linear-gradient(135deg,#7c3aed,#ec4899)', boxShadow:'0 8px 24px rgba(124,58,237,0.5)', marginBottom:16, fontSize:28, fontWeight:900, color:'#fff' }}
            >
              S
            </motion.div>
            <h1 style={{
              fontFamily:"'Cinzel',serif",
              fontSize:'clamp(22px,5vw,30px)', fontWeight:900, margin:'0 0 6px',
              background:'linear-gradient(to right,#a78bfa,#f9a8d4,#60a5fa)',
              WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent',
              letterSpacing:'0.08em',
            }}>
              SURVIVAL GAMES
            </h1>
            <p style={{ color:'rgba(255,255,255,0.45)', fontSize:13, margin:0, letterSpacing:'0.04em' }}>
              Vince chi resiste
            </p>
          </div>

          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:24 }}>
            <div style={{ flex:1, height:1, background:'linear-gradient(to right,transparent,rgba(255,255,255,0.15))' }} />
            <span style={{ color:'rgba(255,255,255,0.2)', fontSize:12 }}>✦</span>
            <div style={{ flex:1, height:1, background:'linear-gradient(to left,transparent,rgba(255,255,255,0.15))' }} />
          </div>

          <form onSubmit={handleLogin}>
            <label style={{ display:'block', fontSize:10, fontWeight:700, letterSpacing:'0.15em', color:'rgba(255,255,255,0.4)', textTransform:'uppercase', marginBottom:8 }}>
              Username
            </label>
            <div style={{ position:'relative', marginBottom:20 }}>
              <input
                id="username"
                data-testid="username-input"
                type="text"
                value={username}
                onChange={e => setUsername(e.target.value)}
                maxLength={20}
                placeholder="Scegli la tua username..."
                autoComplete="off"
                style={{
                  width:'100%', boxSizing:'border-box',
                  padding:'13px 16px',
                  background:'rgba(255,255,255,0.08)',
                  border:'1px solid rgba(255,255,255,0.15)',
                  borderRadius:12, color:'#fff',
                  fontSize:15, fontFamily:"'Outfit',sans-serif",
                  outline:'none', transition:'border-color 0.2s, background 0.2s',
                }}
                onFocus={e => { e.target.style.borderColor='rgba(167,139,250,0.7)'; e.target.style.background='rgba(255,255,255,0.12)'; }}
                onBlur={e => { e.target.style.borderColor='rgba(255,255,255,0.15)'; e.target.style.background='rgba(255,255,255,0.08)'; }}
              />
            </div>

            <AnimatePresence mode="wait">
              <motion.button
                key={username.trim() ? 'active' : 'inactive'}
                data-testid="login-button"
                type="submit"
                disabled={loading || !username.trim()}
                whileHover={username.trim() ? { scale:1.02 } : {}}
                whileTap={username.trim() ? { scale:0.98 } : {}}
                style={{
                  width:'100%', padding:'14px',
                  background: username.trim() ? 'linear-gradient(135deg,#7c3aed,#ec4899,#7c3aed)' : 'rgba(255,255,255,0.07)',
                  border: username.trim() ? 'none' : '1px solid rgba(255,255,255,0.1)',
                  borderRadius:12, fontSize:14, fontWeight:800,
                  color: username.trim() ? '#fff' : 'rgba(255,255,255,0.25)',
                  letterSpacing:'0.06em', textTransform:'uppercase',
                  cursor: loading || !username.trim() ? 'not-allowed' : 'pointer',
                  fontFamily:"'Cinzel',serif",
                  boxShadow: username.trim() ? '0 8px 24px rgba(124,58,237,0.5)' : 'none',
                  transition:'all 0.3s',
                }}
              >
                {loading ? 'Accesso...' : 'Entra nei Giochi'}
              </motion.button>
            </AnimatePresence>
          </form>

          <p style={{ textAlign:'center', marginTop:20, fontSize:11, color:'rgba(255,255,255,0.2)', letterSpacing:'0.06em' }}>
            4 giochi · Multiplayer · Survival
          </p>

          <button
            type="button"
            onClick={() => setShowIntro(true)}
            data-testid="replay-intro"
            style={{
              display:'block', margin:'10px auto 0', background:'none', border:'none',
              color:'rgba(196,181,253,0.45)', fontSize:11, fontWeight:700,
              letterSpacing:'0.1em', cursor:'pointer',
            }}
          >
            ▶ Rivedi l'intro
          </button>
        </div>
      </motion.div>

      <AnimatePresence>
        {showIntro && <IntroSequence onDone={closeIntro} />}
      </AnimatePresence>
    </div>
  );
}
